import { useMemo } from 'react';
import { Training } from '@/types/trainings';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface AvailabilitySlot {
  startTime: string;
  endTime: string;
  slotCount: number;
  standardDuration: number;
}

interface AgendaConfig {
  working_days: string[];
  working_hours: Record<string, { start: string; end: string }>;
  default_duration: number;
  is_public: boolean;
}

// Helper function to convert AM/PM or 24-hour time to 24-hour format
function convertTimeTo24Hour(timeString: string): string {
  if (!timeString) return '00:00';
  
  // If already in 24-hour format (no AM/PM), return as is
  if (!timeString.includes('AM') && !timeString.includes('PM')) {
    // Validate format (HH:MM)
    const parts = timeString.split(':');
    if (parts.length === 2) {
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      if (!isNaN(hours) && !isNaN(minutes) && hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      }
    }
    // Invalid format, return default
    return '00:00';
  }
  
  // Parse AM/PM format
  const [time, period] = timeString.split(' ');
  let [hours, minutes] = time.split(':').map(Number);
  
  if (period === 'PM' && hours !== 12) {
    hours += 12;
  } else if (period === 'AM' && hours === 12) {
    hours = 0;
  }
  // 12 PM deve permanecer 12 (meio-dia)
  // 12 AM deve se tornar 0 (meia-noite)
  
  const result = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  
  return result;
}

export const useAvailabilitySlots = (
  classes: Training[],
  trainings: Training[],
  selectedDate: Date,
  agendaConfig: any
) => {
  const { user } = useAuth();

  return useMemo(() => {
    if (!user || !selectedDate || !agendaConfig) {
      return [];
    }

    // Check if agendaConfig has the required properties
    if (!agendaConfig.working_days || !Array.isArray(agendaConfig.working_days)) {
      return [];
    }

    if (!agendaConfig.working_hours || typeof agendaConfig.working_hours !== 'object') {
      return [];
    }

    const dayOfWeek = selectedDate.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
    
    // Check if the selected day is a working day
    if (!agendaConfig.working_days.includes(dayOfWeek)) {
      return [];
    }

    const workingHours = agendaConfig.working_hours[dayOfWeek];
    if (!workingHours) {
      return [];
    }

    // Convert AM/PM times to 24-hour format for calculations
    const openTime = convertTimeTo24Hour(workingHours.start);
    const closeTime = convertTimeTo24Hour(workingHours.end);
    const standardDuration = agendaConfig.default_duration || 60; // Default to 60 minutes if not set

    // Get all activities for the selected date
    // IMPORTANT: Compare dates in LOCAL timezone to match user's calendar view
    // Classes are stored in UTC, but Date constructor automatically converts to local timezone
    const localDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    const dateString = localDate.toISOString().split('T')[0];
    
    const dayClasses = classes.filter(classItem => {
      if (!classItem.start_time) return false;
      
      // Parse the class date - Date constructor converts UTC to local timezone automatically
      const classDate = new Date(classItem.start_time);
      const classDateString = new Date(classDate.getFullYear(), classDate.getMonth(), classDate.getDate()).toISOString().split('T')[0];
      return classDateString === dateString;
    });

    const dayTrainings = trainings.filter(training => {
      if (!training.start_time) return false;
      
      // Parse the training date - Date constructor converts UTC to local timezone automatically
      const trainingDate = new Date(training.start_time);
      const trainingDateString = new Date(trainingDate.getFullYear(), trainingDate.getMonth(), trainingDate.getDate()).toISOString().split('T')[0];
      return trainingDateString === dateString;
    });

    // Calculate available slots using a simpler approach
    const slots: AvailabilitySlot[] = [];
    
    // Get current time to filter out past slots
    const now = new Date();
    const isToday = now.toDateString() === selectedDate.toDateString();
    // For today, only filter out slots that have already passed
    const currentTimeString = isToday ? 
      `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}` : 
      '00:00';
    
    // Start with opening time
    let currentTime = openTime;
    
    // For today, if current time is within working hours, start from now
    if (isToday && currentTimeString >= openTime && currentTimeString < closeTime) {
      currentTime = currentTimeString;
    }
    
    // Process each activity
    // IMPORTANT: Sort by LOCAL time, not UTC, to match user's timezone
    const allActivities = [...dayClasses, ...dayTrainings].sort((a, b) => {
      if (!a.start_time || !b.start_time) return 0;
      
      // Convert to local Date objects and extract local time
      const dateA = new Date(a.start_time);
      const dateB = new Date(b.start_time);
      const timeA = `${dateA.getHours().toString().padStart(2, '0')}:${dateA.getMinutes().toString().padStart(2, '0')}`;
      const timeB = `${dateB.getHours().toString().padStart(2, '0')}:${dateB.getMinutes().toString().padStart(2, '0')}`;
      return timeA.localeCompare(timeB);
    });

    // If no activities, create slots for the entire working period
    if (allActivities.length === 0) {
      // For today, start from current time if it's within working hours
      // For other days, start from opening time
      let effectiveStartTime = openTime;
      if (isToday) {
        if (currentTimeString >= openTime && currentTimeString < closeTime) {
          // Current time is within working hours, start from now
          effectiveStartTime = currentTimeString;
        } else if (currentTimeString >= closeTime) {
          // Current time is after closing, no slots available
          return [];
        }
        // If current time is before opening, use opening time (already set)
      }
      
      const timeDiff = getTimeDifference(effectiveStartTime, closeTime);
      
      if (timeDiff >= standardDuration) {
        const slotCount = Math.floor(timeDiff / standardDuration);
        
        slots.push({
          startTime: effectiveStartTime,
          endTime: closeTime,
          slotCount,
          standardDuration
        });
      }
      
      return slots;
    }

    // Filter activities to only include those within working hours
    // IMPORTANT: Extract time from ISO string in a timezone-agnostic way
    // We parse the time portion directly from the ISO string to avoid timezone conversion issues
    const activitiesWithinWorkingHours = allActivities.filter(activity => {
      if (!activity.start_time) return false;
      
      // Convert ISO string to Date and extract LOCAL time
      // This ensures we compare local times (from classes) with local times (from working hours)
      const activityStartDate = new Date(activity.start_time);
      // Extract local time (not UTC) to match working hours which are in local time
      const activityStart = `${activityStartDate.getHours().toString().padStart(2, '0')}:${activityStartDate.getMinutes().toString().padStart(2, '0')}`;
      
      // Only include activities that start within working hours
      return activityStart >= openTime && activityStart < closeTime;
    });

    // If no activities within working hours, create slots for the entire working period
    if (activitiesWithinWorkingHours.length === 0) {
      // For today, start from current time if it's within working hours
      // For other days, start from opening time
      let effectiveStartTime = openTime;
      if (isToday) {
        if (currentTimeString >= openTime && currentTimeString < closeTime) {
          // Current time is within working hours, start from now
          effectiveStartTime = currentTimeString;
        } else if (currentTimeString >= closeTime) {
          // Current time is after closing, no slots available
          return [];
        }
        // If current time is before opening, use opening time (already set)
      }
      
      const timeDiff = getTimeDifference(effectiveStartTime, closeTime);
      
      if (timeDiff >= standardDuration) {
        const slotCount = Math.floor(timeDiff / standardDuration);
        
        slots.push({
          startTime: effectiveStartTime,
          endTime: closeTime,
          slotCount,
          standardDuration
        });
      }
      
      return slots;
    }

    for (const activity of activitiesWithinWorkingHours) {
      // Convert ISO strings to Date and extract LOCAL time
      // This ensures we use local times (from classes) to match local times (from working hours)
      const activityStartDate = new Date(activity.start_time);
      const activityEndDate = new Date(activity.end_time);
      
      // Extract local time (not UTC) to match working hours which are in local time
      const activityStart = `${activityStartDate.getHours().toString().padStart(2, '0')}:${activityStartDate.getMinutes().toString().padStart(2, '0')}`;
      const activityEnd = `${activityEndDate.getHours().toString().padStart(2, '0')}:${activityEndDate.getMinutes().toString().padStart(2, '0')}`;
      
      // If there's a gap before the activity, create a slot
      if (currentTime < activityStart) {
        const timeDiff = getTimeDifference(currentTime, activityStart);
        if (timeDiff >= standardDuration) {
          // For today, check if the slot is in the future
          // For other days, always allow slots
          let effectiveStartTime = currentTime;
          if (isToday && currentTimeString > currentTime) {
            effectiveStartTime = currentTimeString;
          }
          
          // Only create slot if there's still time after adjusting for current time
          const effectiveTimeDiff = getTimeDifference(effectiveStartTime, activityStart);
          if (effectiveTimeDiff >= standardDuration) {
            const slotCount = Math.floor(effectiveTimeDiff / standardDuration);
            slots.push({
              startTime: effectiveStartTime,
              endTime: activityStart,
              slotCount,
              standardDuration
            });
          }
        }
      }
      
      // Move current time to after the activity (no buffer)
      if (activityEnd < closeTime) {
        currentTime = activityEnd; // No buffer, use exact end time
      } else {
        // Activity ends at or after closing time, use closing time
        currentTime = closeTime;
      }
    }

    // Add final slot if there's time remaining
    if (currentTime < closeTime) {
      // For today, start from current time if it's later than the last activity
      let effectiveStartTime = currentTime;
      if (isToday && currentTimeString > currentTime) {
        effectiveStartTime = currentTimeString;
      }
      
      const timeDiff = getTimeDifference(effectiveStartTime, closeTime);
      if (timeDiff >= standardDuration) {
        const slotCount = Math.floor(timeDiff / standardDuration);
        slots.push({
          startTime: effectiveStartTime,
          endTime: closeTime,
          slotCount,
          standardDuration
        });
      }
    }

    return slots;
  }, [classes, trainings, selectedDate, user, agendaConfig]);
};

// Helper functions for time calculations
function getTimeDifference(start: string, end: string): number {
  const startMinutes = parseInt(start.split(':')[0]) * 60 + parseInt(start.split(':')[1]);
  const endMinutes = parseInt(end.split(':')[0]) * 60 + parseInt(end.split(':')[1]);
  return endMinutes - startMinutes;
}

function getTimeAfter(time: string, minutes: number): string {
  const totalMinutes = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]) + minutes;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}
